import * as Sentry from "@sentry/browser";
import { UAParser } from "ua-parser-js";
import { isStandalonePWA } from "ua-parser-js/browser-detection";
import * as logger from "../logger";
import { Domain } from "../logger";
import storage from "./../storage";

declare global {
  interface Window {
    VAPID_PUBLIC_KEY: string;
  }
}

interface PwaRequirementState {
  ready: boolean;
  loading: boolean;
  isAdmin: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
  canInstall: boolean;
  installed: boolean;
  isStandalone: boolean;
  browserName: string | null;
  osName: string | null;
  installStep: number | null;
  showPermissions: boolean;
  hasPWARequirements: boolean | null;
  position: object | null;
  lastUpdate: number;
  geolocTTL: number;
  geolocPermissionState: geolocPermissionState | null;
  notificationPermissionState: string | null;
  androidInstall: () => void;
  requestNotificationPermission: () => Promise<void>;
  checkNotificationPermission: () => Promise<void>;
  syncPushSubscription: () => Promise<void>;
  manualInstall: () => void;
  checkPWARequirements: () => void;
  setPermissions: () => void;
  getCurrentPosition: () => void;
  allowGeoloc: () => boolean;
  isValidBrowser: () => boolean;
  copyLink: () => void;
  init: () => Promise<void>;
}

interface geolocPermissionState {
  status: string;
  position: object | null;
  lastUpdate: number | null;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

function manualInstall(this: PwaRequirementState) {
  this.installStep = 1;
}

function checkPWARequirements(this: PwaRequirementState) {
  const hasAdmin = this.isAdmin === true && this.isStandalone === false;

  const hasPwaPermissions =
    this.isStandalone === true &&
    this.allowGeoloc() &&
    this.notificationPermissionState === "granted";

  this.hasPWARequirements = hasAdmin || hasPwaPermissions;
}

function setPermissions(this: PwaRequirementState) {
  this.showPermissions = true;
}

function allowGeoloc(this: PwaRequirementState) {
  return (
    this.geolocPermissionState &&
    this.geolocPermissionState.status === "granted" &&
    this.geolocPermissionState.position
  );
}

function isValidBrowser(this: PwaRequirementState) {
  return (
    (this.browserName === "Safari" && this.osName === "macOS") ||
    (this.browserName === "Mobile Safari" && this.osName === "iOS") ||
    (this.browserName &&
      ["Mobile Chrome", "Chrome"].includes(this.browserName) &&
      this.osName === "Android")
  );
}

async function copyLink() {
  const btn = document.getElementById("copyBtn") as HTMLButtonElement;
  const text = document.getElementById("copyText") as HTMLSpanElement;

  await navigator.clipboard.writeText(window.location.href);

  text.textContent = "Lien copié";

  btn.classList.remove("bg-primary-btn");
  btn.classList.remove("text-white");
  btn.classList.add("bg-white");
  btn.classList.add("text-primary-txt");

  setTimeout(() => {
    text.textContent = "Copier le lien";

    btn.classList.remove("bg-white");
    btn.classList.remove("text-primary-txt");
    btn.classList.add("bg-primary-btn");
    btn.classList.add("text-white");
  }, 2000);
}

async function androidInstall(this: PwaRequirementState) {
  if (!this.deferredPrompt) {
    this.installStep = 1;
    return;
  }

  this.deferredPrompt.prompt();
  await this.deferredPrompt.userChoice;

  this.deferredPrompt = null;
  this.canInstall = false;
}

async function requestNotificationPermission(this: PwaRequirementState) {
  this.loading = true;
  const result = await Notification.requestPermission();
  if (["granted", "denied"].includes(result)) {
    if (result === "granted") {
      subscribeUser();
    }
    this.notificationPermissionState = result;
  }
  this.loading = false;
  this.checkPWARequirements();
}

async function checkNotificationPermission(this: PwaRequirementState) {
  const result = Notification.permission;
  if (["granted", "denied"].includes(result)) {
    this.notificationPermissionState = result;
  }
}

async function syncPushSubscription() {
  try {
    if (Notification.permission === "granted") {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        await subscribeUser();
      }
    }
  } catch (error) {
    logger.error(Domain.App, `Registration failed with ${error}`);
    Sentry.captureException(error);
  }
}

async function subscribeUser() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: window.VAPID_PUBLIC_KEY,
  });

  await fetch("/api/private/push_subscriptions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(subscription.toJSON()),
  });
}

function getCurrentPosition(this: PwaRequirementState) {
  const now = Date.now();
  if (navigator.geolocation) {
    if (this.geolocTTL < now - this.lastUpdate) {
      this.loading = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const position = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this.geolocPermissionState = { status: "granted", position: position, lastUpdate: now };

          storage.setItem(
            {
              key: "geolocPermissionState",
              status: "granted",
              position: position,
              lastUpdate: now,
            },
            { storeName: "main" },
          );

          this.loading = false;
          this.checkPWARequirements();
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            this.geolocPermissionState = { status: "denied", position: null, lastUpdate: null };

            storage.setItem(
              { key: "geolocPermissionState", status: "denied", position: null, lastUpdate: null },
              { storeName: "main" },
            );
          }
          this.loading = false;
        },
        {
          enableHighAccuracy: true,
          timeout: 15_000,
          maximumAge: 0,
        },
      );
    }
  } else {
    this.geolocPermissionState = { status: "not_supported", position: null, lastUpdate: null };
  }
}

function alpineData({ adminStatus }: { adminStatus: boolean }) {
  return {
    ready: false,
    loading: false,
    isAdmin: adminStatus,
    deferredPrompt: null,
    canInstall: false,
    installed: false,
    isStandalone: false,
    browserName: null,
    osName: null,
    installStep: null,
    showPermissions: false,
    hasPWARequirements: null,
    position: null,
    lastUpdate: 0,
    geolocTTL: 300_000,
    geolocPermissionState: null,
    notificationPermissionState: null,
    androidInstall,
    requestNotificationPermission,
    checkNotificationPermission,
    syncPushSubscription,
    manualInstall,
    checkPWARequirements,
    setPermissions,
    getCurrentPosition,
    allowGeoloc,
    isValidBrowser,
    copyLink,
    async init() {
      const parser = new UAParser();
      const results = parser.getResult();
      this.browserName = results.browser.name ?? null;
      this.osName = results.os.name ?? null;
      this.isStandalone = isStandalonePWA();

      window.addEventListener("beforeinstallprompt", (e: Event) => {
        e.preventDefault();
        this.deferredPrompt = e as BeforeInstallPromptEvent;
        this.canInstall = true;
      });

      window.addEventListener("appinstalled", () => {
        this.installed = true;
      });

      if (this.isStandalone) {
        this.geolocPermissionState = (await storage.getItem("geolocPermissionState", {
          storeName: "main",
        })) as geolocPermissionState;

        if (this.geolocPermissionState?.status === "granted") {
          this.getCurrentPosition();
        }

        this.checkNotificationPermission();
        this.syncPushSubscription();
      }
      this.checkPWARequirements();
      this.ready = true;
    },
  } as PwaRequirementState;
}

export default alpineData;
